/**
 * useCloudAssets Hook
 * 
 * Manages cloud-backed assets from IndexedDB (metadata + thumbnails)
 * with on-demand HD fetch from Google Drive
 */

import { useState, useEffect, useCallback } from 'react';
import {
    getAssets,
    getAssetsByProject,
    deleteAsset,
    LocalAssetRecord
} from '../services/localAssetDB';
import { fetchHDFromDrive, getDriveViewLink } from '../services/driveAssetManager';

export interface CloudAsset extends LocalAssetRecord {
    isCloud: true;
    webViewLink: string;
}

interface UseCloudAssetsResult {
    assets: CloudAsset[];
    images: CloudAsset[];
    videos: CloudAsset[];
    isLoading: boolean;
    error: string | null;
    refresh: () => Promise<void>;
    fetchHD: (assetId: string) => Promise<Blob | null>;
    removeAsset: (assetId: string) => Promise<void>;
    getViewLink: (assetId: string) => string | null;
}

/**
 * Hook to access cloud-backed assets stored in IndexedDB
 */
export function useCloudAssets(projectId?: string): UseCloudAssetsResult {
    const [assets, setAssets] = useState<CloudAsset[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Load assets from IndexedDB
    const loadAssets = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);

            let records: LocalAssetRecord[];
            if (projectId) {
                records = await getAssetsByProject(projectId);
            } else {
                records = await getAssets();
            }

            // Convert to CloudAsset format
            const cloudAssets: CloudAsset[] = records.map(r => ({
                ...r,
                isCloud: true as const,
                webViewLink: r.driveFileId ? getDriveViewLink(r.driveFileId) : ''
            }));

            setAssets(cloudAssets);
        } catch (err) {
            console.error('[useCloudAssets] Failed to load:', err);
            setError(err instanceof Error ? err.message : 'Failed to load cloud assets');
        } finally {
            setIsLoading(false);
        }
    }, [projectId]);

    // Initial load
    useEffect(() => {
        loadAssets();
    }, [loadAssets]);

    // Computed: images only
    const images = assets.filter(a => a.type === 'image');

    // Computed: videos only  
    const videos = assets.filter(a => a.type === 'video');

    // Fetch HD version from Drive
    const fetchHD = useCallback(async (assetId: string): Promise<Blob | null> => {
        const asset = assets.find(a => a.id === assetId);
        if (!asset?.driveFileId) {
            console.warn('[useCloudAssets] No driveFileId for asset:', assetId);
            return null;
        }
        return fetchHDFromDrive(asset.driveFileId);
    }, [assets]);

    // Remove asset from local DB
    const removeAsset = useCallback(async (assetId: string): Promise<void> => {
        await deleteAsset(assetId);
        setAssets(prev => prev.filter(a => a.id !== assetId));
    }, []);

    // Get Drive web view link
    const getViewLink = useCallback((assetId: string): string | null => {
        const asset = assets.find(a => a.id === assetId);
        return asset?.webViewLink || null;
    }, [assets]);

    return {
        assets,
        images,
        videos,
        isLoading,
        error,
        refresh: loadAssets,
        fetchHD,
        removeAsset,
        getViewLink
    };
}
